/**
 * Referrals - spec 11's reports (FR-6.3.4–FR-6.3.6).
 *
 * Three views over one table, because the question differs: which PARTNERS
 * members contact, which member LISTINGS they contact, and who ranks highest.
 *
 * Two rules are load-bearing and come from spec 11 rather than from here:
 *
 * 1. **The metric is "Referrals initiated", and it counts TAPS.** Every payload
 *    carries `metric_label` and `metric_note` and this screen renders them
 *    verbatim - including as the table's column header and as the caveat under
 *    it. FR-6.3.3 fixes those words, and the spec 11 handoff requires spec 12 to
 *    render the server's sentence rather than paraphrase it.
 * 2. **The forbidden words are "usage", "orders" and "redemptions."** Nothing
 *    here uses them, and nothing here should: a tap on Call is an intent to make
 *    contact, not a sale. The CSV carries the same caveat as its first line.
 *
 * Ranges are inclusive Nepal calendar days (NFR-10.5.3). A start after the end is
 * refused by the API with a sentence rather than swapped, because an empty report
 * and a reversed one look identical.
 */

import { useState, type ReactNode } from "react";

import { DataTable, PrimaryCell, type Column } from "../components/DataTable";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorNotice,
  EmptyState,
  Field,
  Input,
  LoadingRows,
  Page,
  Select,
  Stat,
  Tabs,
} from "../components/primitives";
import {
  downloadReferralCsv,
  fetchFamilyBizReport,
  fetchLeaderboard,
  fetchVendorReport,
  type FamilyBizReferralRow,
  type LeaderboardEntry,
  type ReferralBreakdown,
  type VendorReferralRow,
} from "../lib/api/referrals";
import { useMutation, useQuery } from "../lib/hooks";
import { formatCount, formatDateTime, kathmanduDaysAgo, kathmanduToday } from "../lib/format";

type View = "vendors" | "listings" | "leaderboard";

export function ReferralsScreen() {
  const [view, setView] = useState<View>("vendors");
  const [fromDate, setFromDate] = useState(kathmanduDaysAgo(29));
  const [toDate, setToDate] = useState(kathmanduToday());

  const range = { from_date: fromDate || undefined, to_date: toDate || undefined };

  return (
    <Page>
      <Card>
        <div className="filters">
          <div style={{ width: 170 }}>
            <Field label="From">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ width: 170 }}>
            <Field label="To">
              {(id) => (
                <Input
                  id={id}
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(event) => setToDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="row row--tight">
            {[
              { label: "7 days", days: 6 },
              { label: "30 days", days: 29 },
              { label: "90 days", days: 89 },
            ].map((preset) => (
              <Button
                key={preset.label}
                size="sm"
                onClick={() => {
                  setFromDate(kathmanduDaysAgo(preset.days));
                  setToDate(kathmanduToday());
                }}
              >
                {preset.label}
              </Button>
            ))}
            <Button
              size="sm"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
              disabled={!fromDate && !toDate}
            >
              All time
            </Button>
          </div>
        </div>
      </Card>

      <Tabs
        value={view}
        onChange={setView}
        options={[
          { value: "vendors", label: "Partners" },
          { value: "listings", label: "Member listings" },
          { value: "leaderboard", label: "Leaderboard" },
        ]}
      />

      {view === "vendors" ? <VendorReport range={range} /> : null}
      {view === "listings" ? <ListingReport range={range} /> : null}
      {view === "leaderboard" ? <LeaderboardReport range={range} /> : null}
    </Page>
  );
}

function ReportShell({
  metricLabel,
  metricNote,
  totals,
  trend,
  message,
  action,
  onExport,
  exporting,
  exportError,
  children,
}: {
  metricLabel: string;
  metricNote: string;
  totals: { referrals_initiated: number; distinct_members: number; breakdown: ReferralBreakdown };
  trend: Array<{ event_date: string; referrals_initiated: number }>;
  message: string | null;
  action: string | null;
  onExport: () => void;
  exporting: boolean;
  exportError: unknown;
  children: ReactNode;
}) {
  return (
    <>
      <Card>
        <CardHeader
          title={metricLabel}
          // The server's caveat, rendered rather than rewritten.
          hint={metricNote}
          actions={
            <Button size="sm" onClick={onExport} disabled={exporting}>
              {exporting ? "Preparing…" : "Download CSV"}
            </Button>
          }
        />
        <div className="row" style={{ gap: 40 }}>
          <Stat label={metricLabel} value={formatCount(totals.referrals_initiated)} />
          <Stat
            label="Distinct members"
            value={formatCount(totals.distinct_members)}
            hint="How many members did the contacting."
          />
          <Stat label="Calls" value={formatCount(totals.breakdown.call)} />
          <Stat label="Emails" value={formatCount(totals.breakdown.email)} />
          <Stat label="Website" value={formatCount(totals.breakdown.website)} />
          <Stat label="Maps" value={formatCount(totals.breakdown.map)} />
        </div>
        {trend.length > 0 ? (
          <div style={{ marginTop: 24 }}>
            <span className="label">Daily trend</span>
            <TrendChart points={trend} label={metricLabel} />
          </div>
        ) : null}
        {exportError ? (
          <div style={{ marginTop: 16 }}>
            <ErrorNotice error={exportError} />
          </div>
        ) : null}
      </Card>

      <Card flush>
        {message ? <p className="notice" style={{ margin: 24 }}>{`${message} ${action ?? ""}`}</p> : null}
        {children}
      </Card>
    </>
  );
}

function VendorReport({ range }: { range: { from_date?: string; to_date?: string } }) {
  const query = useQuery(() => fetchVendorReport(range), [range.from_date, range.to_date]);
  const exportCsv = useMutation(() => downloadReferralCsv("vendor", range));

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={5} columns={5} />
      </Card>
    );
  }
  if (!query.data) return null;

  const columns: Array<Column<VendorReferralRow>> = [
    {
      key: "name",
      header: "Partner",
      render: (row) => (
        <PrimaryCell
          title={row.name}
          detail={row.is_listed ? undefined : "No longer in the member directory"}
        />
      ),
    },
    {
      key: "listed",
      header: "Visibility",
      render: (row) =>
        row.is_listed ? (
          <Badge tone="live">
            <span className="dot" />
            Listed
          </Badge>
        ) : (
          <Badge tone="danger">Not listed</Badge>
        ),
    },
    {
      key: "referrals",
      header: query.data.metric_label,
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.referrals_initiated),
    },
    {
      key: "members",
      header: "Distinct members",
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.distinct_members),
    },
    {
      key: "breakdown",
      header: "Breakdown",
      render: (row) => <BreakdownCell breakdown={row.breakdown} />,
    },
    {
      key: "last",
      header: "Last contact",
      render: (row) =>
        row.last_referral_at ? (
          formatDateTime(row.last_referral_at)
        ) : (
          <span className="text-meta">-</span>
        ),
    },
  ];

  return (
    <ReportShell
      metricLabel={query.data.metric_label}
      metricNote={query.data.metric_note}
      totals={query.data.totals}
      trend={query.data.trend}
      message={query.data.message ?? null}
      action={query.data.action ?? null}
      onExport={() => void exportCsv.run()}
      exporting={exportCsv.pending}
      exportError={exportCsv.error}
    >
      {query.data.items.length === 0 ? (
        <EmptyState
          title="No partner contacts in this period."
          body={query.data.message ?? "Try a wider date range."}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={query.data.items}
          rowKey={(row) => row.vendor_id}
          caption={`${query.data.metric_label}. ${query.data.metric_note}`}
        />
      )}
    </ReportShell>
  );
}

function ListingReport({ range }: { range: { from_date?: string; to_date?: string } }) {
  const query = useQuery(() => fetchFamilyBizReport(range), [range.from_date, range.to_date]);
  const exportCsv = useMutation(() => downloadReferralCsv("family_biz", range));

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={5} columns={5} />
      </Card>
    );
  }
  if (!query.data) return null;

  const columns: Array<Column<FamilyBizReferralRow>> = [
    {
      key: "name",
      header: "Listing",
      render: (row) => (
        <PrimaryCell
          title={row.name}
          detail={row.is_listed ? undefined : "Not currently visible to members"}
        />
      ),
    },
    {
      key: "listed",
      header: "Visibility",
      render: (row) =>
        row.is_listed ? (
          <Badge tone="live">
            <span className="dot" />
            Listed
          </Badge>
        ) : (
          <Badge tone="danger">Not listed</Badge>
        ),
    },
    {
      key: "referrals",
      header: query.data.metric_label,
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.referrals_initiated),
    },
    {
      key: "members",
      header: "Distinct members",
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.distinct_members),
    },
    {
      key: "breakdown",
      header: "Breakdown",
      render: (row) => <BreakdownCell breakdown={row.breakdown} />,
    },
    {
      key: "last",
      header: "Last contact",
      render: (row) =>
        row.last_referral_at ? (
          formatDateTime(row.last_referral_at)
        ) : (
          <span className="text-meta">-</span>
        ),
    },
  ];

  return (
    <ReportShell
      metricLabel={query.data.metric_label}
      metricNote={query.data.metric_note}
      totals={query.data.totals}
      trend={query.data.trend}
      message={query.data.message ?? null}
      action={query.data.action ?? null}
      onExport={() => void exportCsv.run()}
      exporting={exportCsv.pending}
      exportError={exportCsv.error}
    >
      {query.data.items.length === 0 ? (
        <EmptyState
          title="No listing contacts in this period."
          body={query.data.message ?? "Try a wider date range."}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={query.data.items}
          rowKey={(row) => row.family_business_id}
          caption={`${query.data.metric_label}. ${query.data.metric_note}`}
        />
      )}
    </ReportShell>
  );
}

function LeaderboardReport({ range }: { range: { from_date?: string; to_date?: string } }) {
  const [kind, setKind] = useState<"vendor" | "family_biz">("vendor");
  const query = useQuery(() => fetchLeaderboard(kind, range, 25), [kind, range.from_date, range.to_date]);
  const exportCsv = useMutation(() => downloadReferralCsv(kind, range));

  if (query.error) return <ErrorNotice error={query.error} onRetry={query.reload} />;
  if (query.loading && !query.data) {
    return (
      <Card flush>
        <LoadingRows rows={5} columns={4} />
      </Card>
    );
  }
  if (!query.data) return null;

  const columns: Array<Column<LeaderboardEntry>> = [
    { key: "rank", header: "#", label: "Rank", inline: true, align: "right", numeric: true, render: (row) => row.rank },
    {
      key: "name",
      header: kind === "vendor" ? "Partner" : "Member listing",
      render: (row) => row.name,
    },
    {
      key: "listed",
      header: "Visibility",
      render: (row) =>
        row.is_listed ? <Badge tone="live">Listed</Badge> : <Badge tone="danger">Not listed</Badge>,
    },
    {
      key: "referrals",
      header: query.data.metric_label,
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.referrals_initiated),
    },
    {
      key: "members",
      header: "Distinct members",
      align: "right",
      numeric: true,
      render: (row) => formatCount(row.distinct_members),
    },
  ];

  return (
    <Card flush>
      <div style={{ padding: "24px 24px 16px" }}>
        <CardHeader
          title={`Top ${kind === "vendor" ? "partners" : "member listings"}`}
          hint={query.data.metric_note}
          actions={
            <div className="row row--tight">
              <Select
                value={kind}
                onChange={(event) => setKind(event.target.value as "vendor" | "family_biz")}
                aria-label="Ranked by kind"
              >
                <option value="vendor">Partners</option>
                <option value="family_biz">Member listings</option>
              </Select>
              <Button
                size="sm"
                disabled={exportCsv.pending}
                onClick={() => void exportCsv.run()}
              >
                {exportCsv.pending ? "Preparing…" : "Download CSV"}
              </Button>
            </div>
          }
        />
      </div>

      {exportCsv.error ? (
        <div style={{ padding: "0 24px 16px" }}>
          <ErrorNotice error={exportCsv.error} />
        </div>
      ) : null}
      {query.data.items.length === 0 ? (
        <EmptyState
          title="Nothing to rank in this period."
          body={query.data.message ?? "Try a wider date range."}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={query.data.items}
          rowKey={(row) => row.target_id}
          caption={`${query.data.metric_label}, ranked. ${query.data.metric_note}`}
        />
      )}
    </Card>
  );
}

function BreakdownCell({ breakdown }: { breakdown: ReferralBreakdown }) {
  return (
    <span className="text-small text-meta">
      {formatCount(breakdown.call)} call · {formatCount(breakdown.email)} email ·{" "}
      {formatCount(breakdown.website)} web · {formatCount(breakdown.map)} map
    </span>
  );
}

/**
 * A bar per Nepal day.
 *
 * Plain divs rather than a charting library: the series is one number per day and
 * the panel needs to show its shape, not to be explored. Height is a percentage of
 * the busiest day, so a quiet period still reads as a shape rather than as a flat
 * line of invisible bars.
 */
function TrendChart({
  points,
  label,
}: {
  points: Array<{ event_date: string; referrals_initiated: number }>;
  label: string;
}) {
  const busiest = Math.max(...points.map((point) => point.referrals_initiated), 1);

  return (
    <>
      <div className="chart" role="img" aria-label={`Daily ${label.toLowerCase()}`}>
        {points.map((point) => (
          <div
            key={point.event_date}
            className={`chart__bar ${point.referrals_initiated === 0 ? "chart__bar--empty" : ""}`}
            style={{
              height: `${Math.max((point.referrals_initiated / busiest) * 100, point.referrals_initiated === 0 ? 2 : 6)}%`,
            }}
            title={`${point.event_date}: ${point.referrals_initiated}`}
          />
        ))}
      </div>
      <div className="spark-label">
        <span>{points[0]?.event_date ?? ""}</span>
        <span>peak {formatCount(busiest)}/day</span>
        <span>{points[points.length - 1]?.event_date ?? ""}</span>
      </div>
    </>
  );
}
